import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { notificationsApi, NotificationAnalyticsResponse, NotificationAnalyticsParams } from '../../api/notifications';
import { cn } from '../../lib/utils';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { ArrowLeft, Loader2, TrendingUp, Users, CheckCircle2, XCircle, CircleAlert, RotateCcw, Send, Eye } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

interface NotificationAnalyticsPageProps {
  isDark: boolean;
}

export default function NotificationAnalyticsPage({ isDark }: NotificationAnalyticsPageProps) {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [analytics, setAnalytics] = useState<NotificationAnalyticsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<NotificationAnalyticsParams>({});

  useEffect(() => {
    if (id) fetchAnalytics();
  }, [id, filters]);

  const fetchAnalytics = async () => {
    if (!id) return;
    setIsLoading(true);
    setError(null);
    try {
      const data = await notificationsApi.getAnalytics(id, filters);
      setAnalytics(data);
    } catch (error: any) {
      const message = error.response?.data?.message || 'Не удалось загрузить аналитику';
      setError(message);
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return <div className="flex items-center justify-center h-[60vh]"><Loader2 className="w-8 h-8 animate-spin text-[#C9A227]" /></div>;
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 h-[60vh]">
        <CircleAlert className="w-10 h-10 text-red-500" />
        <p className={cn('text-sm', isDark ? 'text-slate-400' : 'text-slate-500')}>{error}</p>
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={() => navigate('/notifications')} className={cn(isDark ? 'border-slate-600 text-white hover:bg-slate-700' : '')}>
            <ArrowLeft className="w-4 h-4 mr-2" />Назад
          </Button>
          <Button onClick={fetchAnalytics} className="bg-[#C9A227] hover:bg-[#C9A227]/90">
            <RotateCcw className="w-4 h-4 mr-2" />Повторить
          </Button>
        </div>
      </div>
    );
  }

  if (!analytics) {
    return <div className="text-center py-12"><p className={cn('text-lg', isDark ? 'text-slate-400' : 'text-slate-500')}>Нет данных аналитики</p></div>;
  }

  const { notification, logs_summary } = analytics;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={() => navigate('/notifications')} className={cn(isDark ? 'text-slate-400 hover:text-white hover:bg-slate-800' : '')}>
          <ArrowLeft className="w-4 h-4 mr-2" />Назад
        </Button>
        <div>
          <h1 className={cn('text-2xl lg:text-3xl font-bold tracking-tight', isDark ? 'text-white' : 'text-[#111111]')}>Аналитика уведомления</h1>
          <p className={cn('text-sm mt-1', isDark ? 'text-slate-400' : 'text-slate-500')}>{notification.title}</p>
        </div>
      </div>

      {/* Filters */}
      <Card className={cn(isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200')}>
        <div className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label className={cn(isDark ? 'text-slate-300' : 'text-slate-700')}>Дата начала</Label>
              <Input type="date" value={filters.start_date || ''} onChange={(e) => setFilters({ ...filters, start_date: e.target.value })} className={cn(isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white')} />
            </div>
            <div className="space-y-2">
              <Label className={cn(isDark ? 'text-slate-300' : 'text-slate-700')}>Дата конца</Label>
              <Input type="date" value={filters.end_date || ''} onChange={(e) => setFilters({ ...filters, end_date: e.target.value })} className={cn(isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white')} />
            </div>
            <div className="flex items-end">
              <Button variant="outline" onClick={() => setFilters({})} className={cn(isDark ? 'border-slate-600 text-white hover:bg-slate-700' : '')}>Сбросить фильтры</Button>
            </div>
          </div>
        </div>
      </Card>

      {/* Сноска для уведомлений, созданных до перехода на модель получателей */}
      {notification.is_legacy && (
        <Card className={cn('border-l-4 border-l-amber-500', isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200')}>
          <div className="p-4 text-sm">
            <p className={cn('font-medium', isDark ? 'text-amber-400' : 'text-amber-600')}>
              Данные до перехода на новую схему
            </p>
            <p className={cn('mt-1', isDark ? 'text-slate-400' : 'text-slate-500')}>
              У этой рассылки «адресовано» совпадает с «получили push»: людей без
              разрешения на уведомления тогда не адресовали вовсе. Открытия пушей
              не отслеживались — ноль здесь означает «не измеряли», а не «никто не открыл».
            </p>
          </div>
        </Card>
      )}

      {/* Воронка: от адресатов до прочтения */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {[
          { label: 'Адресовано', value: notification.recipients_count, icon: Users, color: isDark ? 'text-slate-400' : 'text-slate-400', hint: 'Увидят сообщение в приложении' },
          { label: 'Push отправлен', value: notification.push_sent_count, icon: Send, color: 'text-indigo-500', hint: 'Из них разрешили уведомления' },
          { label: 'Принято Expo', value: notification.accepted_count, icon: CheckCircle2, color: 'text-cyan-500', hint: 'Попыток по устройствам' },
          { label: 'Доставлено', value: notification.delivered_count, rate: notification.delivery_rate, icon: CheckCircle2, color: 'text-green-500', hint: 'Подтверждено квитанцией' },
          { label: 'Открыли push', value: notification.push_opened_count, rate: notification.push_open_rate, icon: TrendingUp, color: 'text-blue-500', hint: 'Тап по уведомлению' },
          { label: 'Прочитали', value: notification.read_count, rate: notification.read_rate, icon: Eye, color: 'text-violet-500', hint: 'Пушем или в приложении' },
        ].map((stat) => (
          <Card key={stat.label} className={cn(isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200')}>
            <div className="p-4">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className={cn('text-sm', isDark ? 'text-slate-400' : 'text-slate-500')}>{stat.label}</p>
                  <p className={cn('text-xl font-bold mt-1', isDark ? 'text-white' : 'text-[#111111]')}>{stat.value.toLocaleString()}</p>
                  {stat.rate !== undefined && <p className={cn('text-xs mt-1', isDark ? 'text-slate-500' : 'text-slate-400')}>{stat.rate.toFixed(1)}%</p>}
                  {stat.hint && <p className={cn('text-[11px] mt-1 leading-tight', isDark ? 'text-slate-600' : 'text-slate-400')}>{stat.hint}</p>}
                </div>
                <stat.icon className={cn('w-6 h-6 shrink-0', stat.color)} />
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className={cn(isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200')}>
          <div className="p-6">
            <h2 className={cn('text-lg font-semibold mb-4', isDark ? 'text-white' : 'text-[#111111]')}>Хронология</h2>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={logs_summary.by_date}>
                <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#475569' : '#e2e8f0'} />
                <XAxis dataKey="date" tick={{ fill: isDark ? '#94a3b8' : '#64748b' }} />
                <YAxis tick={{ fill: isDark ? '#94a3b8' : '#64748b' }} />
                <Tooltip contentStyle={{ backgroundColor: isDark ? '#1e293b' : '#ffffff', border: isDark ? '1px solid #475569' : '1px solid #e2e8f0', color: isDark ? '#ffffff' : '#0f172a' }} />
                <Legend />
                <Line type="monotone" dataKey="sent" stroke="#C9A227" name="Отправлено" />
                <Line type="monotone" dataKey="delivered" stroke="#10b981" name="Доставлено" />
                <Line type="monotone" dataKey="opened" stroke="#3b82f6" name="Открыто" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className={cn(isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200')}>
          <div className="p-6">
            <h2 className={cn('text-lg font-semibold mb-4', isDark ? 'text-white' : 'text-[#111111]')}>По статусам</h2>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={[
                { name: 'Успешно', value: notification.status_breakdown.success },
                { name: 'Ошибка', value: notification.status_breakdown.failed },
                { name: 'Неверный токен', value: notification.status_breakdown.invalid_token },
                { name: 'Не зарегистрирован', value: notification.status_breakdown.device_not_registered },
              ]}>
                <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#475569' : '#e2e8f0'} />
                <XAxis dataKey="name" tick={{ fill: isDark ? '#94a3b8' : '#64748b' }} />
                <YAxis tick={{ fill: isDark ? '#94a3b8' : '#64748b' }} />
                <Tooltip contentStyle={{ backgroundColor: isDark ? '#1e293b' : '#ffffff', border: isDark ? '1px solid #475569' : '1px solid #e2e8f0', color: isDark ? '#ffffff' : '#0f172a' }} />
                <Bar dataKey="value" fill="#C9A227" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      {/* Details */}
      <Card className={cn(isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200')}>
        <div className="p-6">
          <h2 className={cn('text-lg font-semibold mb-4', isDark ? 'text-white' : 'text-[#111111]')}>Детали</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className={cn('text-sm', isDark ? 'text-slate-400' : 'text-slate-500')}>Создано</p>
              <p className={cn('font-medium mt-1', isDark ? 'text-white' : 'text-[#111111]')}>{format(new Date(notification.created_at), 'PPpp', { locale: ru })}</p>
            </div>
            {notification.scheduled_at && (
              <div>
                <p className={cn('text-sm', isDark ? 'text-slate-400' : 'text-slate-500')}>Запланировано</p>
                <p className={cn('font-medium mt-1', isDark ? 'text-white' : 'text-[#111111]')}>{format(new Date(notification.scheduled_at), 'PPpp', { locale: ru })}</p>
              </div>
            )}
            {notification.sent_at && (
              <div>
                <p className={cn('text-sm', isDark ? 'text-slate-400' : 'text-slate-500')}>Отправлено</p>
                <p className={cn('font-medium mt-1', isDark ? 'text-white' : 'text-[#111111]')}>{format(new Date(notification.sent_at), 'PPpp', { locale: ru })}</p>
              </div>
            )}
            {notification.estimated_recipients && (
              <div>
                <p className={cn('text-sm', isDark ? 'text-slate-400' : 'text-slate-500')}>Ожидаемых получателей</p>
                <p className={cn('font-medium mt-1', isDark ? 'text-white' : 'text-[#111111]')}>{notification.estimated_recipients.toLocaleString()}</p>
              </div>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
}
